import "./OutcomeGroupCard.scss";

import clsx from "clsx";
import { type FC, useRef, useState } from "react";

import { serverMoveOutcome, serverMoveOutcomeSubgroup } from "api/api-server-actions";
import { HoverableEditableText } from "components/hover-widgets/EditableText";
import { HoverableToolbar } from "components/hover-widgets/HoverableToolbar";
import { HideUnlessEditModeButton } from "components/materials/HideUnlessEditModeButton";
import { OutcomeCount } from "components/materials/home/admin/outcomes/OutcomeCount";
import { OutcomeGroupMenu } from "components/menus/OutcomeGroupMenu";
import { SidebarOptionsMenu } from "components/menus/SidebarOptionsMenu";
import { OutcomeSubgroupType, OutcomeType } from "components/server-types";
import { t } from "i18n/i18n";
import { Card } from "mds/components/Card";
import { IconText } from "mds/components/IconText";
import { ListItem } from "mds/components/ListItem";
import { MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import { CopySendArrowIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  NestedOutcomeGroupType,
  selectIsEditingAsOrgAdmin,
  selectNestedOutcomeGroups,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { filterMap } from "utils/collections";
import { isTestEnv } from "utils/env";
import { allocateUniqueTitle } from "utils/outcome";
import { useQueryParamsActions } from "utils/routing";
import { onTitleChanged } from "utils/store-utils";

type MoveOutcomeSubMenuProps = {
  outcome: OutcomeType;
  closeMenu?: () => void;
};

const MoveOutcomeSubMenu: FC<MoveOutcomeSubMenuProps> = ({ outcome, closeMenu = () => {} }) => {
  // Nested outcome group tree fully rendered for move targets.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const outcomeGroups = useListSelector(selectNestedOutcomeGroups);
  return (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_to")} />
      }
      overflow="auto"
    >
      {outcomeGroups.flatMap((og) => {
        const subgroups = og.outcomeSubgroups || [];
        if (subgroups.length === 0) {
          if (og.id === outcome.outcome_group_id) {
            return []; // No need to show the current group
          }
          return [
            <ListItem
              key={og.id}
              onClick={async () => {
                await serverMoveOutcome(outcome.id, { group_id: og.id });
                closeMenu();
              }}
            >
              {og.title}
            </ListItem>,
          ];
        }
        return filterMap(
          subgroups,
          (os) => os.id !== outcome.outcome_subgroup_id,
          (os) => (
            <MenuItem
              key={os.id}
              onClick={async () => {
                await serverMoveOutcome(outcome.id, { subgroup_id: os.id });
                closeMenu();
              }}
            >
              {og.title} ➡️ {os.title}
            </MenuItem>
          ),
        );
      })}
    </SubMenu>
  );
};

type MoveOutcomeSubgroupSubMenuProps = {
  outcomeSubgroup: OutcomeSubgroupType;
  closeMenu?: () => void;
};

const MoveOutcomeSubgroupSubMenu: FC<MoveOutcomeSubgroupSubMenuProps> = ({
  outcomeSubgroup,
  closeMenu = () => {},
}) => {
  // Nested outcome group tree fully rendered for move targets.
  // eslint-disable-next-line @minervaproject/no-object-from-use-app-selector/require-properties-on-selector-hooks
  const outcomeGroups = useListSelector(selectNestedOutcomeGroups);
  const validTargetOutcomeGroups = outcomeGroups.filter(
    (og) => (og.outcomes || []).length === 0 && og.id !== outcomeSubgroup.outcome_group_id,
  );
  return (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_to")} />
      }
      overflow="auto"
    >
      {validTargetOutcomeGroups.map((og) => (
        <ListItem
          key={og.id}
          onClick={async () => {
            await serverMoveOutcomeSubgroup(outcomeSubgroup.id, og.id);
            closeMenu();
          }}
        >
          {og.title}
        </ListItem>
      ))}
    </SubMenu>
  );
};

type OutcomeGroupCardProps = {
  outcomeGroup: NestedOutcomeGroupType;
  queryParam: string;
  currentOutcome?: OutcomeType;
};

const onDeleteOutcome = (outcomeId: string) => {
  storeApi.outcomes.destroy(outcomeId);
  trackEvent("Org Outcomes - Delete outcome", {
    ...BUTTON_EVENT_CATEGORY,
  });
};

const onAddOutcomeSubgroup = (
  createOutcomeSubgroupData: { outcome_group_id: string },
  existingTitles: string[],
) => {
  const untitledSubgroup = t("common.untitled.outcome_subgroup");
  const createPromise = storeApi.outcome_subgroups.create({
    ...createOutcomeSubgroupData,
    title: allocateUniqueTitle(untitledSubgroup, existingTitles),
  });
  trackEvent("Org Outcomes - Add subgroup", {
    ...BUTTON_EVENT_CATEGORY,
  });
  return createPromise;
};

const onOutcomeSubgroupDelete = (outcomeSubgroupId: string) => {
  storeApi.outcome_subgroups.destroy(outcomeSubgroupId);
  trackEvent("Org Outcomes - Delete subgroup", {
    ...BUTTON_EVENT_CATEGORY,
  });
};

export const OutcomeGroupCard: FC<OutcomeGroupCardProps> = ({
  outcomeGroup,
  queryParam,
  currentOutcome,
}) => {
  const canEdit = useAppSelector(selectIsEditingAsOrgAdmin);
  const { setQueryParams } = useQueryParamsActions();
  const menuRefOutcomeMap = useRef<Record<string, MenuInstance>>({});
  const menuRefOutcomeSubgroupMap = useRef<Record<string, MenuInstance>>({});
  const [isCreatingOutcomeSubgroup, setIsCreatingOutcomeSubgroup] = useState(false);
  const isCreatingOutcomeSubgroupRef = useRef(false);

  const foundCurrentOutcome =
    currentOutcome?.outcome_group_id === outcomeGroup.id ||
    outcomeGroup.outcomeSubgroups.find((os) => os.id === currentOutcome?.outcome_subgroup_id);

  const onOutcomeClick = (outcome: OutcomeType) => {
    setQueryParams({ [queryParam]: outcome.id });
  };

  const onAddOutcome = (data: { outcome_subgroup_id: string } | { outcome_group_id: string }) => {
    // During normal operation, the outcome's title will be auto-generated by the server
    // and can be omitted from the request params. In unit tests, where server interactions
    // are stubbed out, a placeholder title is required.
    const finalData = isTestEnv ? { ...data, title: "" } : data;
    storeApi.outcomes.create(finalData).then((newOutcome) => {
      onOutcomeClick(newOutcome);
    });
    trackEvent("Org Outcomes - Add outcome", {
      ...BUTTON_EVENT_CATEGORY,
    });
  };

  const onOutcomeGroupDelete = () => {
    storeApi.outcome_groups.destroy(outcomeGroup.id);
    trackEvent("Org Outcomes - Delete group", {
      ...BUTTON_EVENT_CATEGORY,
    });
  };

  const handleAddOutcomeSubgroup = async () => {
    if (isCreatingOutcomeSubgroupRef.current) return;

    isCreatingOutcomeSubgroupRef.current = true;
    setIsCreatingOutcomeSubgroup(true);
    try {
      await onAddOutcomeSubgroup(
        { outcome_group_id: outcomeGroup.id },
        outcomeGroup.outcomeSubgroups.map((subgroup) => subgroup.title),
      );
    } finally {
      isCreatingOutcomeSubgroupRef.current = false;
      setIsCreatingOutcomeSubgroup(false);
    }
  };

  return (
    <Card className="outcome-group-card mb-3 mt-2" isActive={!!foundCurrentOutcome}>
      <div className="flex w-full items-center justify-between">
        <HoverableToolbar
          className="mb-1 ml-1 flex w-full items-center justify-between"
          extraListItems={
            <>
              {outcomeGroup.outcomes?.length === 0 &&
                outcomeGroup.outcomeSubgroups?.length === 0 && (
                  <li>
                    <OutcomeGroupMenu
                      buttonKind="tertiary"
                      isAddingOutcomeSubgroup={isCreatingOutcomeSubgroup}
                      onAddOutcome={() => onAddOutcome({ outcome_group_id: outcomeGroup.id })}
                      onAddOutcomeSubgroup={handleAddOutcomeSubgroup}
                    />
                  </li>
                )}
              <li>
                <SidebarOptionsMenu
                  optionsMenuActions={{
                    onDelete: onOutcomeGroupDelete,
                  }}
                />
              </li>
            </>
          }
          readOnly={!canEdit}
          reference="outcome_group"
          showEditButton
        >
          <h3 className="my-0 w-full">
            <HoverableEditableText
              className="w-full"
              label={t("glossary.outcome_group_title")}
              textSize="h3"
              value={outcomeGroup.title}
              disableUnlessEditMode
              shouldWrap
              onValueChanged={(newTitle) =>
                onTitleChanged("outcome_groups", outcomeGroup.id, newTitle, {
                  eventName: "Org Outcomes - Change group title",
                })
              }
            />
          </h3>
        </HoverableToolbar>

        <OutcomeCount count={outcomeGroup.outcomeCount} />
      </div>

      <div className="flex flex-col gap-4">
        {outcomeGroup.outcomeSubgroups.map((outcomeSubgroup) => (
          <div key={outcomeSubgroup.id}>
            <HoverableToolbar
              className="mb-1 ml-1 flex w-full items-center justify-between"
              extraListItems={
                <li>
                  <SidebarOptionsMenu
                    instanceRef={(menu) => {
                      if (menu) {
                        menuRefOutcomeSubgroupMap.current[outcomeSubgroup.id] = menu;
                      }
                    }}
                    optionsMenuActions={{
                      onDelete: () => onOutcomeSubgroupDelete(outcomeSubgroup.id),
                    }}
                  >
                    <MoveOutcomeSubgroupSubMenu
                      closeMenu={() =>
                        menuRefOutcomeSubgroupMap.current[outcomeSubgroup.id].closeMenu()
                      }
                      outcomeSubgroup={outcomeSubgroup}
                    />
                  </SidebarOptionsMenu>
                </li>
              }
              readOnly={!canEdit}
              reference="outcome_subgroup"
              showEditButton
              onClickAdd={() => onAddOutcome({ outcome_subgroup_id: outcomeSubgroup.id })}
            >
              <HoverableEditableText
                className="w-full"
                label={t("glossary.outcome_subgroup_title")}
                textSize="h4"
                value={outcomeSubgroup.title}
                shouldWrap
                onValueChanged={(newTitle) =>
                  onTitleChanged("outcome_subgroups", outcomeSubgroup.id, newTitle, {
                    eventName: "Org Outcomes - Change subgroup title",
                  })
                }
              />
            </HoverableToolbar>

            {outcomeSubgroup.outcomes.map((outcome: OutcomeType) => (
              <ListItem
                className="w-full"
                isSelected={currentOutcome?.id === outcome.id}
                key={outcome.id}
                label={outcome.title}
                onClick={() => onOutcomeClick(outcome)}
              >
                <HoverableToolbar
                  className="flex w-full items-center justify-between"
                  extraListItems={
                    <li>
                      <SidebarOptionsMenu
                        instanceRef={(menu) => {
                          if (menu) {
                            menuRefOutcomeMap.current[outcome.id] = menu;
                          }
                        }}
                        optionsMenuActions={{
                          onDelete: () => onDeleteOutcome(outcome.id),
                        }}
                      >
                        <MoveOutcomeSubMenu
                          closeMenu={() => menuRefOutcomeMap.current[outcome.id].closeMenu()}
                          outcome={outcome}
                        />
                      </SidebarOptionsMenu>
                    </li>
                  }
                  readOnly={!canEdit}
                >
                  <div className="body-s ml-4 flex w-full items-center">
                    #
                    <div className="outcome-title my-1 w-full cursor-pointer truncate px-2">
                      {outcome.title}
                    </div>
                  </div>
                </HoverableToolbar>
              </ListItem>
            ))}

            {canEdit && (
              <HideUnlessEditModeButton
                className="add-subgroup-outcome"
                reference="outcome"
                onClick={() => onAddOutcome({ outcome_subgroup_id: outcomeSubgroup.id })}
              />
            )}
          </div>
        ))}
      </div>

      {outcomeGroup.outcomes.map((outcome: OutcomeType) => (
        <ListItem
          className="w-full"
          isSelected={currentOutcome?.id === outcome.id}
          key={outcome.id}
          label={outcome.title}
          onClick={() => onOutcomeClick(outcome)}
        >
          <HoverableToolbar
            className="flex w-full items-center justify-between"
            extraListItems={
              <li>
                <SidebarOptionsMenu
                  instanceRef={(menu) => {
                    if (menu) {
                      menuRefOutcomeMap.current[outcome.id] = menu;
                    }
                  }}
                  optionsMenuActions={{
                    onDelete: () => onDeleteOutcome(outcome.id),
                  }}
                >
                  <MoveOutcomeSubMenu
                    closeMenu={() => menuRefOutcomeMap.current[outcome.id].closeMenu()}
                    outcome={outcome}
                  />
                </SidebarOptionsMenu>
              </li>
            }
            readOnly={!canEdit}
          >
            <div className="body-s ml-1 flex w-full items-center">
              #
              <div className="outcome-title my-1 w-full cursor-pointer truncate px-2">
                {outcome.title}
              </div>
            </div>
          </HoverableToolbar>
        </ListItem>
      ))}

      {outcomeGroup.outcomeSubgroups?.length === 0 && canEdit && (
        <HideUnlessEditModeButton
          className="add-group-outcome"
          reference="outcome"
          onClick={() => onAddOutcome({ outcome_group_id: outcomeGroup.id })}
        />
      )}

      {outcomeGroup.outcomes?.length === 0 && canEdit && (
        <HideUnlessEditModeButton
          className={clsx("add-subgroup", outcomeGroup.outcomeSubgroups?.length > 0 && "mt-4")}
          isDisabled={isCreatingOutcomeSubgroup}
          reference="outcome_subgroup"
          onClick={handleAddOutcomeSubgroup}
        />
      )}
    </Card>
  );
};
